import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { JustifiedAction } from '../actions/JustifiedAction';
import type { SanctionsRow as Row } from './sanctionsQueueModel';
import {
  SANCTIONS_LIST_LABEL,
  SANCTIONS_STATUS_COLOR,
  SANCTIONS_STATUS_LABEL,
} from './sanctionsState';

/**
 * One row of the sanctions-screening queue (D-sanctions). The engine-written match (member,
 * jurisdiction, score, the sanctioned name it resembles, when it was screened), the derived member
 * sanctions standing, and the review actions where the compliance capability permits.
 *
 * Reviewing is a RECORDED DETERMINATION through the chokepoint with a MANDATORY reason
 * (JustifiedAction): `clear` resolves a false positive, `confirm` a true match. Confirming writes
 * ONLY the match status (sanctionsStatus = 'confirmed'); it does NOT flip fraudStatus and moves no
 * value. The match score / name / list are engine-written and never set here.
 */
export const SanctionsRow = ({ row, now }: { row: Row; now: number }) => {
  const { role } = useAccessContext();
  const can = (capability: Parameters<typeof hasAccess>[1]['capability']) =>
    hasAccess({ role, accessibleRegions: [] }, { capability });

  const iso = new Date(now).toISOString();
  const isPending = row.sanctionsStatus === 'pending_review';

  const clearData = (reason: string): Record<string, unknown> => ({
    sanctionsStatus: 'cleared',
    reviewReason: reason,
    reviewedAt: iso,
  });
  const confirmData = (reason: string): Record<string, unknown> => ({
    sanctionsStatus: 'confirmed',
    reviewReason: reason,
    reviewedAt: iso,
  });
  const prev: Record<string, unknown> = {
    id: row.id,
    sanctionsStatus: 'pending_review',
    reviewReason: null,
    reviewedAt: null,
  };

  return (
    <Card
      variant="outlined"
      data-testid={`sanctions-match-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {row.memberName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.memberEmail} | {row.market.toUpperCase()}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            label={SANCTIONS_LIST_LABEL[row.matchedList]}
            data-testid={`sanctions-list-${row.id}`}
          />
          <Chip
            size="small"
            variant="filled"
            color={SANCTIONS_STATUS_COLOR[row.sanctionsStatus]}
            label={SANCTIONS_STATUS_LABEL[row.sanctionsStatus]}
            data-testid={`sanctions-status-${row.id}`}
          />
          {row.memberSanctionsFlagged && (
            <Chip
              size="small"
              variant="filled"
              color="error"
              label="Member sanctions flagged"
              data-testid={`sanctions-flagged-${row.id}`}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid={`sanctions-score-${row.id}`}
          >
            score {row.matchScore} | matches {row.matchedName} | screened{' '}
            {row.screenedAt.slice(0, 10)}
          </Typography>
        </Stack>

        {row.reviewReason && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1 }}
            data-testid={`sanctions-reason-${row.id}`}
          >
            Review note: {row.reviewReason}
          </Typography>
        )}

        {isPending && (can('sanctions:clear') || can('sanctions:confirm')) && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ mt: 2 }}
            useFlexGap
            flexWrap="wrap"
          >
            {can('sanctions:clear') && (
              <JustifiedAction
                resource="sanctionsMatches"
                capability="sanctions:clear"
                id={row.id}
                label="Clear (false positive)"
                color="primary"
                dialogTitle="Clear this match as a false positive"
                dialogIntro="Use this when the match is a name collision and the member is not the listed individual. Record the basis for clearing; it must be defensible."
                buildData={(reason) => clearData(reason)}
                previousData={prev}
                successMessage="Match cleared"
              />
            )}
            {can('sanctions:confirm') && (
              <JustifiedAction
                resource="sanctionsMatches"
                capability="sanctions:confirm"
                id={row.id}
                label="Confirm match"
                color="error"
                dialogTitle="Confirm this as a true match"
                dialogIntro="Use this when the member is the listed individual. This records a sanctions determination; downstream systems enforce the block and any payout hold. Record the basis."
                buildData={(reason) => confirmData(reason)}
                previousData={prev}
                successMessage="Match confirmed"
              />
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
